import type { TimeFormat } from '../types/domain';

/**
 * Konverze mezi ms (number) a textovym formatem M:SS,cc nebo SS,cc (Czech).
 * Format per disciplina v `Discipline.timeFormat`.
 */

export const formatTime = (ms: number | null, fmt: TimeFormat = 'MM_SS_CC'): string => {
  if (ms == null) return '—';
  const totalCs = Math.round(ms / 10);
  const cs = totalCs % 100;
  const ccStr = cs.toString().padStart(2, '0');
  if (fmt === 'SS_CC') {
    // SS_CC zobrazuje celkove sekundy, klidne i pres 60 (napr. 80,45).
    const totalSec = Math.floor(totalCs / 100);
    return `${totalSec.toString().padStart(2, '0')},${ccStr}`;
  }
  // MM:SS,CC — sekundy rolluji do minut
  const min = Math.floor(totalCs / 6000);
  const sec = Math.floor(totalCs / 100) % 60;
  return `${min}:${sec.toString().padStart(2, '0')},${ccStr}`;
};

export const parseTime = (input: string): number | null => {
  if (!input) return null;
  const cleaned = input.replace(/\./g, ',').trim();
  // Bez minut povolime libovolny pocet sekund (napr. "80,45" v SS_CC formatu).
  // S minutami (MM:SS,cc) sekundy musi byt < 60.
  const m = cleaned.match(/^(?:(\d+):)?(\d{1,4}),(\d{1,2})$/);
  if (!m) return null;
  const min = m[1] ? Number(m[1]) : 0;
  const sec = Number(m[2]);
  const cs = Number(m[3].padEnd(2, '0').slice(0, 2));
  if (m[1] != null && sec >= 60) return null;
  return min * 60_000 + sec * 1_000 + cs * 10;
};

/**
 * Max delka digit bufferu. Univerzalni pro oba formaty — uzivatel muze zadat cas
 * v setinach (vse co roluje zprava) a displej ho automaticky rozlozi na m/s/cs.
 * 6 cifer = max 9999,99 setin = 99:59,99.
 */
export const DIGIT_CAPACITY = 6;
export const digitCapacity = (_fmt?: TimeFormat): number => DIGIT_CAPACITY;

/**
 * Displej pro digit buffer — "roluje" zprava.
 * Buffer reprezentuje celkove setiny; zobrazi se podle fmt.
 * Priklad: buffer "6856" -> totalCs=6856 -> 1 min, 8 s, 56 cs -> MM_SS_CC: "1:08,56", SS_CC (fallback): "1:08,56".
 * Priklad: buffer "2537" -> totalCs=2537 -> 0 min, 25 s, 37 cs -> MM_SS_CC: "0:25,37", SS_CC: "25,37".
 */
/**
 * Stopky-styl: cifry rolluji zprava do pevnych pozic (cs | ss | mm), bez scitaci logiky.
 * Priklad v MM_SS_CC:
 *   "10988" -> mm=1, ss=09, cc=88  -> "1:09,88"
 *   "988"   -> mm=0, ss=09, cc=88  -> "0:09,88"
 *   "5"     -> mm=0, ss=00, cc=05  -> "0:00,05"
 * V SS_CC tvori zbyle cifry celkove sekundy (bez rolloveru na minuty pri typing).
 */
export const formatDigitsAsDisplay = (digits: string, fmt: TimeFormat): string => {
  const clean = digits.replace(/\D/g, '').slice(-DIGIT_CAPACITY);
  if (clean.length === 0) {
    return fmt === 'SS_CC' ? '00,00' : '0:00,00';
  }
  const padded = clean.padStart(2, '0');
  const cs = padded.slice(-2);
  const rest = padded.slice(0, -2);
  if (fmt === 'SS_CC') {
    const sec = rest.length === 0 ? 0 : Number(rest);
    return `${sec.toString().padStart(2, '0')},${cs}`;
  }
  // MM_SS_CC — predposledni 2 cifry = ss (pozicove, bez rolloveru), zbytek = mm
  const ssPadded = rest.padStart(2, '0');
  const ss = ssPadded.slice(-2);
  const mm = ssPadded.length > 2 ? Number(ssPadded.slice(0, -2)) : 0;
  return `${mm}:${ss},${cs}`;
};

/**
 * Digit buffer -> ms (stopky styl, pozicove cs | ss | mm).
 * V MM_SS_CC se sekundy nad 60 NE-rolluji do minut pri typing, ale ms je absolutni,
 * takze ss=60 znamena 60 s (a formatTime po ulozeni zobrazi jako 1:00,00).
 * Prazdny buffer = null.
 */
export const parseTimeFromDigits = (digits: string, fmt?: TimeFormat): number | null => {
  const clean = digits.replace(/\D/g, '').slice(-DIGIT_CAPACITY);
  if (clean.length === 0) return null;
  const padded = clean.padStart(2, '0');
  const cs = Number(padded.slice(-2));
  const rest = padded.slice(0, -2);
  if (fmt === 'SS_CC') {
    const sec = rest.length === 0 ? 0 : Number(rest);
    return sec * 1_000 + cs * 10;
  }
  const ssPadded = rest.padStart(2, '0');
  const ss = Number(ssPadded.slice(-2));
  const mm = ssPadded.length > 2 ? Number(ssPadded.slice(0, -2)) : 0;
  return mm * 60_000 + ss * 1_000 + cs * 10;
};

/**
 * ms -> digit buffer pro inicializaci z existujici hodnoty.
 * Vraci cifry ve formatu MMSSCC (MM_SS_CC) nebo SS...CC (SS_CC), bez leading nul.
 */
export const msToDigits = (ms: number | null, fmt?: TimeFormat): string => {
  if (ms == null) return '';
  const totalCs = Math.round(ms / 10);
  if (totalCs === 0) return '';
  if (fmt === 'SS_CC') {
    const sec = Math.floor(totalCs / 100);
    const cs = totalCs % 100;
    if (sec === 0) {
      const s = cs.toString();
      return s.slice(-DIGIT_CAPACITY);
    }
    const s = `${sec}${cs.toString().padStart(2, '0')}`;
    return s.slice(-DIGIT_CAPACITY);
  }
  // MM_SS_CC — rollover (ss < 60), display bude "spravne" 1:09,88 i pro bufferu "10988"
  const min = Math.floor(totalCs / 6000);
  const sec = Math.floor(totalCs / 100) % 60;
  const cs = totalCs % 100;
  const ccStr = cs.toString().padStart(2, '0');
  const ssStr = sec.toString().padStart(2, '0');
  if (min === 0 && sec === 0) {
    return cs === 0 ? '' : ccStr.replace(/^0+/, '') || '0';
  }
  if (min === 0) {
    // "SSCC" bez leading zeros u ss (ale ss je padded 2, takze odstranime jen pokud je 0X)
    const leading = ssStr.replace(/^0/, '');
    return `${leading}${ccStr}`.slice(-DIGIT_CAPACITY);
  }
  return `${min}${ssStr}${ccStr}`.slice(-DIGIT_CAPACITY);
};
